import React from 'react';
import {
  Container,
  FormField,
  Input,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { QuestionFormParams } from '../QuestionFormWizard';
import DocumentLink, { DocumentLinkItem } from '../DocumentLink';
import { DocLink, Question } from 'src/types/helios/Question';
import RequiredFieldLabel from 'src/components/RequiredFieldLabel';

/** Form `DocLink` → the editor's row shape. Keeps the source-SOP marker. */
export function toDocumentLinkItem(doc: DocLink): DocumentLinkItem {
  return {
    heading: doc.title || 'Documentation',
    title: doc.title || '',
    url: doc.link || '',
    ...(doc.isSourceSop ? { isSourceSop: true } : {}),
  };
}

/** Editor row → form `DocLink`. Keeps the source-SOP marker. */
export function toFormDocLink(link: DocumentLinkItem): DocLink {
  return {
    title: link.title || link.heading,
    link: link.url,
    ...(link.isSourceSop ? { isSourceSop: true } : {}),
  };
}

interface DocumentationProps {
  formData: QuestionFormParams;
  setFormData: React.Dispatch<React.SetStateAction<QuestionFormParams>>;
  question?: Question;
  isCopyQuestion?: boolean;
  fieldValidationError: Record<string, string>;
}

const Documentation: React.FC<DocumentationProps> = ({
  formData,
  setFormData,
  question,
  isCopyQuestion = false,
  fieldValidationError,
}) => {
  return (
    <SpaceBetween size="l" data-testid="documentation-space-between">
      <DocumentLink
        data-testid="document-link"
        // Both projections carry `isSourceSop` (only when true, so ordinary
        // links keep their exact `{ title, link }` shape). Without this, any
        // add/remove would rewrite every row and silently drop the source-SOP
        // marker from an Astra question being edited.
        links={(formData.docLinks || []).map(toDocumentLinkItem)}
        onChange={(links) =>
          setFormData((prev) => ({
            ...prev,
            docLinks: links.map(toFormDocLink),
          }))
        }
      />
      {question && !isCopyQuestion && (
        <Container data-testid="change-summary-container">
          <FormField
            data-testid="change-summary-field"
            label={<RequiredFieldLabel label="Change Summary" />}
            errorText={fieldValidationError.changeLog}
            stretch
          >
            <Textarea
              data-testid="change-summary-textarea"
              value={formData.changeLog || ''}
              onChange={({ detail }) =>
                setFormData((prev) => ({
                  ...prev,
                  changeLog: detail.value,
                }))
              }
            />
          </FormField>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default Documentation;
